import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import About from "./pages/About.tsx";
import Contact from "./pages/Contact";
import Home from "./pages/Home.tsx";
import VirtualArchives from "./pages/VirtualArchives.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.tsx";
import FaqPage from "./pages/FaqPage.tsx";
import IndustryExamples from "./pages/IndustryExamples.tsx";
import BookAssessment from "./pages/BookAssessment.tsx";
import BookReview from "./pages/BookReview.tsx";
import BookManage from "./pages/BookManage.tsx";
import Catalog from "./pages/Catalog.tsx";

// Auth-gated routes that are never prerendered (not in reactSnap.include), so
// lazy-loading them trims the initial bundle without risking react-snap capturing
// a loading fallback on any indexed page.
const LoginSignup = lazy(() => import("./pages/Authentication.tsx"));
const Account = lazy(() => import("./pages/Account.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
import { RequireAuth } from "./components/RequireAuth.tsx";
import Gradient from "./components/Gradient.tsx";
import { LandingProvider } from "./contexts/LandingContext";
import NotFound from "./pages/NotFound.tsx";
import { catalogApi } from "./services/catalogApi";

const App: React.FC = () => {
  // Our dashboard can be asleep; wake it on the first page view so the catalog and booking are quick to load.
  useEffect(() => catalogApi.warm(), []);

  return (
    <AuthProvider>
      <LandingProvider>
          <Header />
          <main className="min-h-screen">
            <div className="w-full h-full fixed top-0 left-0 -z-10 opacity-[0.22]">
              <Gradient
                color1="#ae488e"
                color2="#fafafa"
                color3="#fafafa"
                timeSpeed={1}
                colorBalance={0}
                warpStrength={1}
                warpFrequency={5}
                warpSpeed={2}
                warpAmplitude={50}
                blendAngle={0}
                blendSoftness={0.35}
                rotationAmount={500}
                noiseScale={2}
                grainAmount={0.1}
                grainScale={2}
                grainAnimated
                contrast={1.5}
                gamma={1}
                saturation={1}
                centerX={0}
                centerY={0}
                zoom={0.9}
              />
            </div>
            <ScrollToTop />
            <Suspense fallback={<div className="min-h-[calc(100vh-4rem)]" aria-hidden="true" />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/industry-examples" element={<IndustryExamples />} />
              <Route path="/virtual-archives" element={<VirtualArchives />} />
              <Route path="/services/virtual-archives" element={<Navigate to="/virtual-archives" replace />} />
              <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/book" element={<BookAssessment />} />
              <Route path="/book/review" element={<BookReview />} />
              <Route path="/book/manage" element={<BookManage />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/auth" element={<LoginSignup />} />
              <Route path="/auth/reset-password/:uid/:token" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </main>
          <Footer />
      </LandingProvider>
    </AuthProvider>
  );
};

export default App;
